import { createAsyncThunk } from '@reduxjs/toolkit';
import { getProjectBankForms } from 'scenes/ProjectBank/services/getProjectBankForms.service';
import { selectIsBankKeptToImages } from 'scenes/ProjectBank/store/selectors/project-bank-access.selectors';
import { buildProjectBankFormItems } from 'scenes/ProjectBank/utils/projectBankForms';
import { selectProjects } from 'store/projects/selectors/projectsSelectors';

const getProjectFolderForForms = (state, projectId) => {
  const folderEntities =
    state.projectBank.projectFolderFilePermissions.entities || {};

  return Object.values(folderEntities).find((folder) => {
    if (!folder || folder.type !== 'FOLDER' || !folder.key) {
      return false;
    }

    const keyDepth = folder.key.split('/').length;
    const isProjectFolder =
      (folder.businessId && keyDepth === 3) ||
      (!folder.businessId && keyDepth === 2);

    return isProjectFolder && folder.project === projectId;
  });
};

/**
 * Loads a project's forms and builds them into the bank's virtual forms
 * folders and files (utils/projectBankForms).
 *
 * Not while the bank is kept to its images folder (a gallery-only user,
 * utils/bankAccess — whom the API refuses them — or the bank open as the
 * project's gallery): the forms are no part of the gallery, so nothing is
 * asked. On a fresh page load the bank may start before the permissions tell
 * who the user is; the request that goes out then may be refused once, and
 * its rejection is left quiet — nothing waits on it and no notice is shown.
 * @param {string} projectId
 */
export const loadProjectBankFormsFolderContents = createAsyncThunk(
  'projectBank/loadProjectBankFormsFolderContents',
  async (projectId, { getState }) => {
    let nextToken = null;
    const forms = [];
    const templatesByGroupId = {};
    const usersById = {};

    do {
      const page = await getProjectBankForms(projectId, { nextToken });

      (page?.items || []).forEach((form) => forms.push(form));

      (page?.templates || []).forEach((template) => {
        if (template?.template_group_id) {
          templatesByGroupId[template.template_group_id] = template;
        }
      });

      (page?.users || []).forEach((user) => {
        if (user?.id) {
          usersById[user.id] = user;
        }
      });

      nextToken = page?.nextToken || null;
    } while (nextToken);

    const state = getState();
    const projects = selectProjects(state);
    const projectFolder = getProjectFolderForForms(state, projectId);
    const project =
      projects[projectId] ||
      (typeof projectFolder?.project === 'object'
        ? projectFolder.project
        : null);

    const { folders, files } = buildProjectBankFormItems({
      forms,
      project,
      projectFolder,
      templatesByGroupId,
      usersById,
    });

    return {
      projectId,
      folders,
      files,
      // Built from the project entity and its root folder; without either
      // nothing was built, and the project's forms are not to count as
      // loaded (a later request, once the entity is here, builds them).
      complete: Boolean(project && projectFolder),
    };
  },
  {
    condition: (_, { getState }) => !selectIsBankKeptToImages(getState()),
  }
);
